import React, { useState, useRef, useEffect, useCallback } from 'react';
import { Trash2 } from 'lucide-react';
import { ReactSortable } from 'react-sortablejs';
import { useHistory, useLocation } from 'react-router-dom';
import AlertNotification from '../../components/AlertNotification.js';
import DiscardConfirmationModal from '../../components/DiscardConfirmationModal.js';
import UnsavedChangesPrompt from '../../components/UnsavedChangesPrompt.js';
import { Routes } from '../../routes';
import apiHelper from '../../apiHelper';
import FinalResultRuleTooltip from '../../components/FinalResultRuleTooltip.js';
import CustomPortal from '../../components/CustomPortal.js';
import { FINAL_RESULT_RULES, ROUNDING_ONLY, normalizeFinalResultRule } from '../../utils/finalResultRule.js';
import {
    FORMULA_TYPES, FEE_FLAT, FEE_PCT_PLATFORM,
    MARKUP_TYPE_OPTIONS, MARKUP_NONE, MARKUP_FIXED, MARKUP_PERCENT,
    feeFormulaType, normalizeMarkups,
    markupBounds, fmtBound, calculatePlatformPrice,
} from '../../utils/pricingTemplate.js';

let _rowId = 0;
const makeRow = (platform_fee_name = '', formula_type = FEE_PCT_PLATFORM) => ({
    id: ++_rowId,
    platform_fee_name,
    formula_type,
    value: '',
    cap: '',
});

let _markupId = 0;
// A band stores only its own upper bound and whether that bound is inclusive.
// upper === null on the last band means unbounded (∞).
const makeMarkup = (type = '', includeUpper = false) => ({
    id: ++_markupId,
    upper: null,
    upperText: '',
    includeUpper,
    type,
    param: null,
    touched: {},
});

// Per-band validation: a band is wrong when its range contradicts the one
// before it, when a non-final band has no upper bound at all, or when the
// chosen Markup Type is missing the value it needs.
const bandErrors = (list, i) => {
    const r = list[i];
    const isLast = i === list.length - 1;
    const b = markupBounds(list, i);
    const rangeInvalid = b.upper != null && b.lower != null &&
        (b.upper < b.lower || (b.upper === b.lower && !(b.lowerInclusive && b.upperInclusive)));
    const rangeRequired = !isLast && r.upper == null;
    const t = r.touched || {};
    return {
        rangeInvalid,
        rangeRequired,
        rangeError: !!t.range && (rangeInvalid || rangeRequired),
        rangeErrorMsg: rangeRequired ? 'Range is required' : (rangeInvalid ? 'Range is invalid' : ''),
        typeError: !!t.type && !r.type,
        valueError: !!t.value && (r.type === MARKUP_FIXED || r.type === MARKUP_PERCENT) && r.param == null,
    };
};

// Save-time check: same rules, but independent of what the user has touched.
const hasBandProblem = list => list.some((r, i) => {
    const e = bandErrors(list, i);
    const needsParam = (r.type === MARKUP_FIXED || r.type === MARKUP_PERCENT) && r.param == null;
    return e.rangeInvalid || e.rangeRequired || !r.type || needsParam;
});

const numericFilter = val => {
    const stripped = val.replace(/[^0-9.]/g, '');
    const parts = stripped.split('.');
    return parts.length > 1 ? parts[0] + '.' + parts.slice(1).join('') : stripped;
};

// ERP-5169 — no digit field on this form accepts a negative. A price, a markup and a
// range bound are all meaningless below zero, and the markup ladder had no save-time
// guard at all, so "-19" entered here was stored verbatim.
//
// Blocked in two places on purpose: onKeyDown so the character never appears as the
// user types, and again in the change handler because paste, drag-and-drop and
// autofill all set a value without firing a keydown.
const blockMinusKey = e => { if (e.key === '-') e.preventDefault(); };
const stripMinus = val => String(val ?? '').replace(/-/g, '');

const fmtRate = val => {
    if (!val || isNaN(parseFloat(val))) return val;
    const dotIdx = val.indexOf('.');
    const decimals = dotIdx === -1 ? 0 : val.length - dotIdx - 1;
    const capped = decimals > 5 ? parseFloat(val).toFixed(5) : val;
    return String(parseFloat(capped));
};

const money = n => (isNaN(n) ? '0.00' : Math.abs(n).toFixed(2));

const SgdInput = ({ value, onValueChange, onBlur, error, disabled }) => (
    <div style={{ display: 'flex', alignItems: 'center', border: `1px solid ${error ? '#DC3545' : '#6C757D'}`, borderRadius: 8, overflow: 'hidden', height: 38, background: disabled ? '#F1F3F5' : '#fff' }}>
        <span style={{ padding: FEE_INPUT_PAD, color: '#212529', fontSize: 14, borderRight: '1px solid #6C757D', background: '#F8F9FA', height: '100%', display: 'flex', alignItems: 'center', whiteSpace: 'nowrap', flexShrink: 0, boxSizing: 'border-box' }}>
            SGD
        </span>
        <input
            type="text"
            inputMode="decimal"
            value={value}
            disabled={disabled}
            onChange={e => onValueChange(numericFilter(e.target.value))}
            onKeyDown={blockMinusKey}
            onBlur={onBlur}
            style={{ border: 'none', outline: 'none', flex: 1, padding: FEE_INPUT_PAD, fontSize: 14, height: '100%', background: 'transparent', minWidth: 0, fontFamily: 'Montserrat', boxSizing: 'border-box' }}
        />
    </div>
);

const PctInput = ({ value, onValueChange, onBlur, error }) => (
    <div style={{ display: 'flex', alignItems: 'center', border: `1px solid ${error ? '#DC3545' : '#6C757D'}`, borderRadius: 8, overflow: 'hidden', height: 38, background: '#fff' }}>
        <input
            type="text"
            inputMode="decimal"
            value={value}
            onChange={e => onValueChange(numericFilter(e.target.value))}
            onKeyDown={blockMinusKey}
            onBlur={onBlur}
            style={{ border: 'none', outline: 'none', flex: 1, padding: FEE_INPUT_PAD, fontSize: 14, height: '100%', background: 'transparent', minWidth: 0, fontFamily: 'Montserrat', boxSizing: 'border-box' }}
        />
        <span style={{ padding: FEE_INPUT_PAD, color: '#212529', fontSize: 14, borderLeft: '1px solid #6C757D', background: '#F8F9FA', height: '100%', display: 'flex', alignItems: 'center', flexShrink: 0, boxSizing: 'border-box' }}>%</span>
    </div>
);

// The "clear" cross. Stroke is a prop because the Markup Type field carries it
// at full contrast while the inline field clears stay grey.
const ClearIcon = ({ stroke = '#212529' }) => (
    <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 12 12" fill="none">
        <path d="M9 3L3 9M3 3L9 9" stroke={stroke} strokeWidth="1.33333" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
);

// The Markup Type field's disclosure chevron.
const ChevronIcon = ({ open }) => (
    <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 18 18" fill="none"
        style={{ transition: 'transform 0.2s', transform: open ? 'rotate(180deg)' : 'rotate(0deg)', flexShrink: 0 }}>
        <path d="M4.5 6.75L9 11.25L13.5 6.75" stroke="#212529" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
);

// The solid caret on the Add Platform Fee button.
const Caret = ({ open, size = 10 }) => (
    <svg width={size} height={size * 0.6} viewBox="0 0 10 6" fill="#212529" xmlns="http://www.w3.org/2000/svg"
        style={{ transition: 'transform 0.2s', transform: open ? 'rotate(180deg)' : 'rotate(0deg)', flexShrink: 0 }}>
        <path d="M0 0L5 6L10 0H0Z" />
    </svg>
);

const MENU_ROW_STYLE = {
    display: 'block', width: '100%', padding: '15px 10px', textAlign: 'left',
    background: 'none', border: 'none', cursor: 'pointer',
    fontSize: 14, fontWeight: 400, color: '#212529', fontFamily: 'Montserrat',
};

const MENU_PANEL_STYLE = {
    background: '#fff', border: '1px solid #E9ECEF', borderRadius: 8,
    boxShadow: '0 4px 12px rgba(0,0,0,0.10)', overflow: 'hidden',
};

// Markup Type picker. A native <select> cannot be styled to match the "Add
// Platform Fee" menu, so this is a button plus a panel that reuses the same
// card, row padding and pale-yellow hover.
//
// The panel is portalled to the body and positioned from the trigger's own
// rect: the markup table scrolls horizontally, and an absolutely positioned
// menu would be clipped by that scroll container rather than overlapping the
// rows below it.
const MarkupTypeSelect = ({ value, error, onChange, onTouch }) => {
    const [open, setOpen] = useState(false);
    const [rect, setRect] = useState(null);
    const triggerRef = useRef(null);
    const menuRef = useRef(null);

    // The blank option means "not chosen yet" — it is offered as the "×" clear
    // rather than as an empty row in the menu.
    const options = MARKUP_TYPE_OPTIONS.filter(o => o.value);
    const selected = options.find(o => o.value === value);

    // Held in a ref so `close` can stay identity-stable while still calling the
    // latest onTouch — the listener effect below depends on `close`, and an
    // identity that churned every render would re-add the listeners each time.
    const onTouchRef = useRef(onTouch);
    useEffect(() => { onTouchRef.current = onTouch; });

    const close = useCallback(() => {
        setOpen(false);
        if (onTouchRef.current) onTouchRef.current();
    }, []);

    useEffect(() => {
        if (!open) return;

        const reposition = () => {
            if (triggerRef.current) setRect(triggerRef.current.getBoundingClientRect());
        };
        const onPointerDown = ev => {
            if (triggerRef.current && triggerRef.current.contains(ev.target)) return;
            if (menuRef.current && menuRef.current.contains(ev.target)) return;
            close();
        };
        const onKeyDown = ev => { if (ev.key === 'Escape') close(); };

        // Capture phase so the inner scrollers (the page body, the table's own
        // horizontal scroller) are heard, not just the window.
        window.addEventListener('scroll', reposition, true);
        window.addEventListener('resize', reposition);
        document.addEventListener('mousedown', onPointerDown);
        document.addEventListener('keydown', onKeyDown);
        return () => {
            window.removeEventListener('scroll', reposition, true);
            window.removeEventListener('resize', reposition);
            document.removeEventListener('mousedown', onPointerDown);
            document.removeEventListener('keydown', onKeyDown);
        };
    }, [open, close]);

    const toggle = () => {
        if (open) { close(); return; }
        if (triggerRef.current) setRect(triggerRef.current.getBoundingClientRect());
        setOpen(true);
    };

    return (
        <>
            <button
                type="button"
                ref={triggerRef}
                onClick={toggle}
                style={{
                    display: 'flex', alignItems: 'center', gap: 8,
                    width: '100%', height: 40, padding: 10, boxSizing: 'border-box',
                    borderRadius: 8, background: '#fff', cursor: 'pointer',
                    border: '1px solid ' + (error ? '#DC3545' : '#212529'),
                    fontFamily: 'Montserrat', fontSize: 14, fontWeight: 400, textAlign: 'left',
                }}
            >
                <span style={{ flex: '1 1 0%', minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', color: selected ? '#212529' : '#6C757D' }}>
                    {selected ? selected.label : 'Select...'}
                </span>
                {selected && (
                    <span
                        role="button"
                        title="Clear"
                        onMouseDown={ev => { ev.preventDefault(); ev.stopPropagation(); }}
                        onClick={ev => { ev.stopPropagation(); onChange(''); close(); }}
                        style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: 12, height: 12, cursor: 'pointer', flexShrink: 0 }}
                    >
                        <ClearIcon />
                    </span>
                )}
                <ChevronIcon open={open} />
            </button>

            {open && rect && (
                <CustomPortal>
                    <div
                        ref={menuRef}
                        style={{
                            ...MENU_PANEL_STYLE,
                            position: 'fixed', top: rect.bottom + 4, left: rect.left,
                            minWidth: rect.width, zIndex: 2000,
                        }}
                    >
                        {options.map(o => (
                            <button
                                key={o.value}
                                type="button"
                                style={{ ...MENU_ROW_STYLE, background: o.value === value ? '#FFFEF0' : 'none' }}
                                onMouseEnter={ev => ev.currentTarget.style.background = '#FFFEF0'}
                                onMouseLeave={ev => ev.currentTarget.style.background = o.value === value ? '#FFFEF0' : 'none'}
                                onClick={() => { onChange(o.value); close(); }}
                            >
                                {o.label}
                            </button>
                        ))}
                    </div>
                </CustomPortal>
            )}
        </>
    );
};

const errText = { color: '#DC3545', fontSize: 11, marginTop: 3, fontFamily: 'Montserrat' };

// Cell shell vs the control inside it — kept as constants so the two paddings
// stay in step across the fee rows.
const FEE_CELL_PAD = '15px 10px';
const FEE_INPUT_PAD = '10px';

// Test Calculation is two equal columns. Column one carries the Base Price
// field, panels 01/02 and Platform Price (Final); column two carries the action
// buttons, panel 03 and Expected Profit. All three rows share this one grid so
// they cannot drift out of alignment.
//
// Two explicit columns rather than auto-fit: the card is wide enough that
// auto-fit with a 320px minimum would lay out three columns and split the
// panels apart. minmax(0, 1fr) lets a column shrink below its content instead
// of overflowing the card.
const TC_GAP = 16;
const TC_GRID = { display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', gap: TC_GAP };

// Breakdown panel palette. Both panels share it — the left one is not a
// different colour from the right.
const PANEL_BG = '#FFFEF0';
const PANEL_BORDER = '#CC9A06';
const PANEL_RULE = '#000';
const PANEL_LABEL = '#CC9A06';
const PANEL_GREEN = '#146C43';

// One line inside a Test Calculation breakdown panel. `value == null` renders
// "-", which is what every line shows before Calculate has produced a result
// and what a fee line shows when no fee items exist.
//
// Border weight: a plain fee line carries no rule at all — the fee list reads
// as one unbroken group. `boldBorder` marks the two rules that frame that
// group (under the header row, and under the last fee row); `last` drops the
// rule entirely for a section's closing total.
const DetailLine = ({ label, value, sign = '', strong, color, note, last, boldBorder }) => (
    <div style={{
        display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: 12,
        padding: '7px 0',
        borderBottom: last ? 'none' : `${boldBorder ? 2 : 1}px solid ${PANEL_RULE}`,
        fontFamily: 'Montserrat', fontSize: 12,
        fontWeight: strong ? 700 : 400,
        color: color || '#212529',
    }}>
        <span>
            {label}
            {note && <span style={{ color: '#ADB5BD', fontWeight: 400, fontStyle: 'italic', marginLeft: 6, fontSize: 11 }}>{note}</span>}
        </span>
        <span style={{ whiteSpace: 'nowrap', fontVariantNumeric: 'tabular-nums' }}>
            {value == null ? '-' : `${sign}SGD ${value}`}
        </span>
    </div>
);

// A dashed rule between two PanelLabel sub-sections within the same panel —
// lighter-touch than the solid fee-group rules, since it separates steps
// rather than framing a total.
const StepDivider = () => (
    <div style={{ borderTop: `1px dashed ${PANEL_RULE}`, margin: '10px 0' }} />
);

// "01 Reverse-Derive ..." — a large, muted ordinal beside a small bold gold
// heading. The two halves carry deliberately different weight so the number
// reads as a step marker, not part of the heading's own type.
const PanelLabel = ({ n, children, style }) => (
    <div style={{ display: 'flex', gap: 8, alignItems: 'baseline', marginBottom: 4, fontFamily: 'Montserrat', ...style }}>
        <span style={{ fontSize: 20, fontWeight: 600, color: '#ADB5BD', lineHeight: 1 }}>{n}</span>
        <span style={{ fontSize: 12, fontWeight: 700, color: PANEL_LABEL }}>{children}</span>
    </div>
);

export default function AddPricingTemplate() {
    const history = useHistory();
    const location = useLocation();
    const searchParams = new URLSearchParams(location.search);
    const isEdit = searchParams.has('edit');
    const editId = searchParams.get('edit');
    const pageTitle = isEdit ? 'Edit Pricing Template' : 'Add Pricing Template';
    const breadcrumbs = ['Settings', 'Pricing Template', pageTitle];

    const [formData, setFormData] = useState({ name: '', status: 'active', final_result_rule: ROUNDING_ONLY, notes: '' });
    const [rows, setRows] = useState(isEdit ? [makeRow()] : [
        makeRow('Transaction Fee', FEE_PCT_PLATFORM),
        makeRow('Commission Fee', FEE_PCT_PLATFORM),
        makeRow('Others', FEE_FLAT),
    ]);
    // Always at least one band, and the last one runs to ∞.
    const [markups, setMarkups] = useState([makeMarkup(MARKUP_NONE)]);

    const [pageLoading, setPageLoading] = useState(isEdit);
    const [errors, setErrors] = useState({});
    const [touched, setTouched] = useState({});
    const [isDirty, setIsDirty] = useState(false);
    const [isDiscarding, setIsDiscarding] = useState(false);
    const [discardModalOpen, setDiscardModalOpen] = useState(false);
    const [alert, setAlert] = useState({ show: false, type: '', message: '' });
    const [addFeeOpen, setAddFeeOpen] = useState(false);
    const addFeeRef = useRef(null);

    const [testBasePrice, setTestBasePrice] = useState('');
    const [detailOpen, setDetailOpen] = useState(false);
    const [calc, setCalc] = useState(null);

    const [testBaseErr, setTestBaseErr] = useState('');

    // ── Test Calculation ───────────────────────────────────────────────────
    // Calculate doubles as the validation trigger for the whole template: it
    // checks the Platform Fee rows, the Platform Price Markup ladder and Base
    // Price, and refuses to produce a figure while any of them is wrong.
    const runCalculation = () => {
        touchAll();
        const rowErrs = validateRowsAndUpdate();
        const bandProblem = hasBandProblem(markups);

        const base = parseFloat(testBasePrice);
        const baseBad = !String(testBasePrice).trim() || isNaN(base) || base <= 0;
        setTestBaseErr(baseBad ? 'Base Price is required' : '');

        if (Object.keys(rowErrs).length > 0 || bandProblem || baseBad) {
            setCalc(null);
            return;
        }

        setCalc(calculatePlatformPrice(rows, markups, base, formData.final_result_rule));
    };

    const ruleLabel = (FINAL_RESULT_RULES.find(r => r.value === formData.final_result_rule) || {}).label || '';

    // ── Validation ─────────────────────────────────────────────────────────
    const validateRowsAndUpdate = (rowList = rows, markupList = markups) => {
        const rowErrs = {};
        const seenNames = {};

        rowList.forEach(row => {
            const name = row.platform_fee_name.trim();
            if (!name) {
                rowErrs[`row_${row.id}_platform_fee`] = 'Platform Fee is required';
            } else {
                const key = name.toLowerCase();
                if (seenNames[key] !== undefined) {
                    rowErrs[`row_${row.id}_platform_fee`] = 'Platform Fee already exists';
                    rowErrs[`row_${seenNames[key]}_platform_fee`] = 'Platform Fee already exists';
                } else {
                    seenNames[key] = row.id;
                }
            }
            if (!row.value) {
                rowErrs[`row_${row.id}_value`] = row.formula_type === FEE_FLAT ? 'Price is required' : 'Rate is required';
            }
        });

        setErrors(prev => {
            const next = {};
            Object.entries(prev).forEach(([k, v]) => { if (!k.startsWith('row_')) next[k] = v; });
            return { ...next, ...rowErrs };
        });

        return rowErrs;
    };

    useEffect(() => {
        const handler = e => {
            if (addFeeRef.current && !addFeeRef.current.contains(e.target)) setAddFeeOpen(false);
        };
        if (addFeeOpen) document.addEventListener('mousedown', handler);
        return () => document.removeEventListener('mousedown', handler);
    }, [addFeeOpen]);

    useEffect(() => {
        if (!isEdit || !editId) return;
        const fetchTemplate = async () => {
            try {
                const res = await apiHelper(`sales/pricing-templates/${editId}/`, 'GET');
                const json = await res.json();
                if (!res.ok) { showAlert('danger', json.error || 'Failed to load template.'); setPageLoading(false); return; }
                setFormData({
                    name: json.template_name || '',
                    status: json.status || 'active',
                    final_result_rule: normalizeFinalResultRule(json.final_result_rule),
                    notes: json.notes || '',
                });
                const feeSrc = json.fees || json.rows || [];
                if (feeSrc.length > 0) {
                    setRows(feeSrc.map(f => {
                        const formula_type = feeFormulaType(f);
                        const rawValue = f.fee_value !== undefined ? String(f.fee_value) : String(f.value || '');
                        const rawCap   = f.cap_amount !== undefined ? (f.cap_amount ? String(f.cap_amount) : '') : String(f.cap || '');
                        const fmtValue = formula_type === FEE_PCT_PLATFORM
                            ? fmtRate(rawValue)
                            : (rawValue && !isNaN(parseFloat(rawValue)) ? parseFloat(rawValue).toFixed(2) : rawValue);
                        const fmtCap   = rawCap && !isNaN(parseFloat(rawCap)) ? parseFloat(rawCap).toFixed(2) : rawCap;
                        return { id: ++_rowId, platform_fee_name: f.platform_fee_name || '', formula_type, value: fmtValue, cap: fmtCap };
                    }));
                }
                const mkSrc = normalizeMarkups(json.markups);
                if (mkSrc.length > 0) {
                    setMarkups(mkSrc.map(m => ({
                        ...m,
                        id: ++_markupId,
                        upperText: m.upper == null ? '' : Number(m.upper).toFixed(2),
                        touched: {},
                    })));
                }
            } catch {
                showAlert('danger', 'Failed to load template.');
            } finally {
                setPageLoading(false);
            }
        };
        fetchTemplate();
    }, []);

    const showAlert = (type, message) => {
        setAlert({ show: true, type, message });
        setTimeout(() => setAlert({ show: false, type: '', message: '' }), 4000);
    };

    const handleChange = e => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
        setIsDirty(true);
        if (errors[name]) setErrors(prev => ({ ...prev, [name]: '' }));
    };

    const handleRowChange = (id, field, value) => {
        setRows(prev => prev.map(r => r.id === id ? { ...r, [field]: value } : r));
        setIsDirty(true);
        const errKey = field === 'platform_fee_name' ? `row_${id}_platform_fee` : `row_${id}_${field}`;
        if (errors[errKey]) setErrors(prev => ({ ...prev, [errKey]: '' }));
    };

    const addRow = formula_type => {
        setRows(prev => [...prev, makeRow('', formula_type)]);
        setIsDirty(true);
    };

    const removeRow = id => {
        setRows(prev => prev.filter(r => r.id !== id));
        setIsDirty(true);
    };

    // ── Markup band handlers (mirroring the reference prototype) ───────────
    const markupTouch = (i, field) => {
        setMarkups(prev => prev.map((r, idx) =>
            idx === i ? { ...r, touched: { ...(r.touched || {}), [field]: true } } : r));
    };

    // upperText keeps the raw keystrokes so a half-typed "16." is not snapped
    // back mid-edit; upper holds the parsed number the maths uses.
    const updateUpper = (i, val) => {
        const v = parseFloat(val);
        setMarkups(prev => prev.map((r, idx) =>
            idx === i ? { ...r, upper: isNaN(v) ? null : v, upperText: val } : r));
        setIsDirty(true);
    };

    const blurUpper = i => {
        setMarkups(prev => prev.map((r, idx) => idx === i
            ? { ...r, upperText: r.upper == null ? '' : Number(r.upper).toFixed(2), touched: { ...(r.touched || {}), range: true } }
            : r));
    };

    const toggleUpper = i => {
        setMarkups(prev => prev.map((r, idx) => idx === i ? { ...r, includeUpper: !r.includeUpper } : r));
        setIsDirty(true);
    };

    const updateType = (i, val) => {
        setMarkups(prev => prev.map((r, idx) => idx === i ? { ...r, type: val } : r));
        setIsDirty(true);
    };

    const updateParam = (i, val) => {
        const v = parseFloat(val);
        setMarkups(prev => prev.map((r, idx) => idx === i ? { ...r, param: isNaN(v) ? null : v } : r));
        setIsDirty(true);
    };

    // Deleting the unbounded band promotes its predecessor: that band inherits
    // the open-ended upper bound, so its now-meaningless bound and range error
    // are cleared rather than left showing.
    const removeMarkup = i => {
        setMarkups(prev => {
            if (prev.length <= 1) return prev;
            const next = prev.filter((_, idx) => idx !== i);
            if (i === prev.length - 1 && next.length > 0) {
                const promoted = next[next.length - 1];
                next[next.length - 1] = {
                    ...promoted, upper: null, includeUpper: false, upperText: '',
                    touched: { ...(promoted.touched || {}), range: false },
                };
            }
            return next;
        });
        setIsDirty(true);
    };

    // The last band is always the unbounded one, so a new band is inserted
    // *before* it rather than appended — otherwise the open-ended range would
    // end up in the middle of the ladder.
    const addMarkup = () => {
        setMarkups(prev => {
            const next = prev.slice();
            const last = next[next.length - 1];
            if (last.upper === null && next.length > 1) {
                next.splice(next.length - 1, 0, makeMarkup('', true));
            } else {
                next.push(makeMarkup('', false));
            }
            return next;
        });
        setIsDirty(true);
    };

    const touchAll = (rowList = rows) => {
        const allTouched = {};
        rowList.forEach(row => {
            allTouched[`row_${row.id}_platform_fee`] = true;
            allTouched[`row_${row.id}_value`] = true;
        });
        setTouched(prev => ({ ...prev, ...allTouched }));
        setMarkups(prev => prev.map(r => ({ ...r, touched: { range: true, type: true, value: true } })));
    };

    const checkDuplicateName = async (nameValue) => {
        const trimmed = nameValue.trim();
        if (!trimmed) return false;
        try {
            const params = new URLSearchParams({ search: trimmed, page_size: 200 });
            const checkRes = await apiHelper(`sales/pricing-templates/?${params}`, 'GET');
            if (checkRes.ok) {
                const checkJson = await checkRes.json();
                const lowerName = trimmed.toLowerCase();
                const isDupe = (checkJson.results || []).some(t => {
                    if (t.archived_at || t.archived_by || t.archived_by_full_name || t.archived_by_name) return false;
                    const tName = (t.template_name || '').toLowerCase().trim();
                    if (tName !== lowerName) return false;
                    if (isEdit && String(t.pricing_template_id) === String(editId)) return false;
                    return true;
                });
                if (isDupe) {
                    setErrors(prev => ({ ...prev, name: 'Template Name already exists' }));
                    return true;
                }
            }
        } catch { /* ignore */ }
        return false;
    };

    const handleSave = async () => {
        touchAll();
        const formErrs = {};
        if (!formData.name.trim()) formErrs.name = 'Template Name is required';
        setErrors(prev => ({ ...prev, ...formErrs }));

        const rowErrs = validateRowsAndUpdate();
        const bandProblem = hasBandProblem(markups);

        if (Object.keys(formErrs).length > 0 || Object.keys(rowErrs).length > 0 || bandProblem) {
            showAlert('danger', 'Error Saving');
            return;
        }

        try {
            const isDupe = await checkDuplicateName(formData.name);
            if (isDupe) { showAlert('danger', 'Error Saving'); return; }
        } catch { /* proceed to save */ }

        try {
            const payload = {
                template_name: formData.name.trim(),
                status: formData.status,
                final_result_rule: formData.final_result_rule,
                notes: formData.notes.trim(),
                rows: rows.map(r => ({
                    platform_fee_name: r.platform_fee_name.trim(),
                    formula_type: r.formula_type,
                    value: r.value,
                    cap: r.cap,
                })),
                markups: markups.map((m, idx) => ({
                    sort_order: idx,
                    upper_bound: idx === markups.length - 1 ? null : m.upper,
                    upper_inclusive: m.includeUpper,
                    markup_type: m.type,
                    value: m.param,
                })),
            };

            const endpoint = isEdit
                ? `sales/pricing-templates/update/${editId}/`
                : 'sales/pricing-templates/create/';
            const res = await apiHelper(endpoint, 'POST', payload);
            const json = await res.json();

            if (!res.ok) {
                const isDuplicateName =
                    (json.template_name && Array.isArray(json.template_name)) ||
                    (typeof json.error === 'string' && /exists|unique|duplicate/i.test(json.error)) ||
                    (typeof json.detail === 'string' && /exists|unique|duplicate/i.test(json.detail));
                if (isDuplicateName) {
                    setErrors(prev => ({ ...prev, name: 'Template Name already exists' }));
                    showAlert('danger', 'Error Saving');
                } else {
                    showAlert('danger', json.error || 'Error saving. Please try again.');
                }
                return;
            }

            setIsDiscarding(true);
            setIsDirty(false);
            setTimeout(() => history.push(Routes.PricingTemplate.path, { success: isEdit ? 'Pricing Template Updated Successfully' : 'Pricing Template Saved Successfully' }), 100);
        } catch (err) {
            showAlert('danger', 'Error saving. Please try again.');
        }
    };

    const handleDiscardConfirm = confirm => {
        if (confirm) {
            setIsDiscarding(true);
            setIsDirty(false);
            setDiscardModalOpen(false);
            setTimeout(() => history.push(Routes.PricingTemplate.path, { success: 'Pricing Template Discarded Successfully' }), 100);
        } else {
            setDiscardModalOpen(false);
        }
    };

    const platformPriceChipStyle = {
        display: 'inline-flex', alignItems: 'center', gap: 4,
        color: '#0D6EFD', fontSize: 13, fontWeight: 500, whiteSpace: 'nowrap',
    };

    const sectionHeadStyle = { fontSize: 12, fontWeight: 700, color: '#212529', fontFamily: 'Montserrat' };

    if (pageLoading) {
        return (
            <div className="company-body main-company-body-inner new-look" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%' }}>
                <span style={{ fontFamily: 'Montserrat', color: '#6C757D', fontSize: 14 }}>Loading...</span>
            </div>
        );
    }

    return (
        <div className="company-body main-company-body-inner new-look" style={{ display: 'flex', flexDirection: 'column', height: '100%', overflow: 'hidden' }}>
            {alert.show && (
                <AlertNotification type={alert.type} message={alert.message} onClose={() => setAlert({ show: false, type: '', message: '' })} />
            )}

            {/* Breadcrumb */}
            <div className="breadcrumb">
                {breadcrumbs.map((crumb, idx) => (
                    <span
                        key={idx}
                        className={`breadcrumb-item ${crumb === 'Pricing Template' ? 'clickable' : ''}`}
                        onClick={() => crumb === 'Pricing Template' && history.push(Routes.PricingTemplate.path)}
                        style={{ cursor: crumb === 'Pricing Template' ? 'pointer' : 'default' }}
                    >
                        {crumb}{idx < breadcrumbs.length - 1 ? ' ' : ''}
                    </span>
                ))}
            </div>

            {/* Title + action buttons */}
            <div className="edit-organisation-info">
                <div className="edit-organisation-left">
                    <h1 style={{color:'#212529'}}>{pageTitle}</h1>
                </div>
                <div className="discard-save-organisation-right d-flex align-items-center gap-2">
                    <button
                        className="dark-button"
                        type="button"
                        style={{ display: 'flex', height: 34, minWidth: 100, padding: '8px', justifyContent: 'center', alignItems: 'center', gap: 10, borderRadius: 8 }}
                        onClick={() => setDiscardModalOpen(true)}
                    >
                        Discard
                    </button>
                    <DiscardConfirmationModal
                        isOpen={discardModalOpen}
                        onCancel={() => setDiscardModalOpen(false)}
                        onConfirm={handleDiscardConfirm}
                        entityName="Pricing Template"
                    />
                    <button
                        className="dark-border"
                        type="button"
                        style={{ display: 'flex', height: 34, minWidth: 100, padding: '8px', justifyContent: 'center', alignItems: 'center', gap: 10, borderRadius: 8, boxShadow: '0 1px 4px 0 rgba(0,0,0,0.10)' }}
                        onClick={handleSave}
                    >
                        Save
                    </button>
                </div>
            </div>

            {/* Scrollable content */}
            <div style={{ flex: 1, overflowY: 'auto', paddingBottom: 24 }}>
                <div className="info-section">

                    {/* ── Basic Info ── */}
                    <div className="info-card new-info-overflow-auto">
                        <div className="info-header">
                            <h4 className="info-title">Basic Info</h4>
                        </div>
                        <div className="info-content d-block">
                            <div className="grid-info-adding">
                                <div className="row gappingcls-added">
                                    <div className="col-lg-6 col-md-6 col-12">
                                        <div className="info-inner">
                                            <label className="form-label d-flex align-items-center gap-1">
                                                Template Name <span className="error-color">*</span>
                                            </label>
                                            <input
                                                type="text"
                                                className="form-control common-input-all"
                                                name="name"
                                                value={formData.name}
                                                onChange={handleChange}
                                                onBlur={() => checkDuplicateName(formData.name)}
                                                style={errors.name ? { borderColor: '#dc3545' } : { border: '1px solid #6C757D' }}
                                            />
                                            {errors.name && (
                                                <div style={{ fontSize: 12, color: '#DC3545', fontFamily: 'Montserrat', marginTop: 5 }}>
                                                    {errors.name}
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                    <div className="col-lg-6 col-md-6 col-12">
                                        <div className="info-inner">
                                            <label className="form-label">
                                                Status <span className="error-color">*</span>
                                            </label>
                                            <div className="flexing-type-radio">
                                                <div className="form-check">
                                                    <input className="form-check-input" type="radio" name="status" id="statusActive" value="active" checked={formData.status === 'active'} onChange={handleChange} />
                                                    <label className="form-check-label" htmlFor="statusActive" style={{ fontWeight: 'normal', fontSize: 14 }}>Active</label>
                                                </div>
                                                <div className="form-check">
                                                    <input className="form-check-input" type="radio" name="status" id="statusInactive" value="inactive" checked={formData.status === 'inactive'} onChange={handleChange} />
                                                    <label className="form-check-label" htmlFor="statusInactive" style={{ fontWeight: 'normal', fontSize: 14 }}>Inactive</label>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                                <div className="row gappingcls-added">
                                    <div className="col-lg-6 col-md-6 col-12">
                                        <div className="info-inner">
                                            <label className="form-label d-flex align-items-center gap-1">
                                                Final Result Rule <span className="error-color">*</span>
                                                <FinalResultRuleTooltip />
                                            </label>
                                            <div className="flexing-type-radio">
                                                {FINAL_RESULT_RULES.map(rule => (
                                                    <div className="form-check" key={rule.value}>
                                                        <input
                                                            className="form-check-input"
                                                            type="radio"
                                                            name="final_result_rule"
                                                            id={`finalResultRule_${rule.value}`}
                                                            value={rule.value}
                                                            checked={formData.final_result_rule === rule.value}
                                                            onChange={handleChange}
                                                        />
                                                        <label className="form-check-label" htmlFor={`finalResultRule_${rule.value}`} style={{ fontWeight: 'normal', fontSize: 14 }}>
                                                            {rule.label}
                                                        </label>
                                                    </div>
                                                ))}
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* ── Platform Fee ── */}
                    <div className="info-card new-info-overflow-auto">
                        <div className="info-header">
                            <h4 className="info-title">Platform Fee</h4>
                        </div>
                        <div className="info-content d-block">

                            <div style={{ overflowX: 'auto', borderBottom: '1px solid #DEE2E6', marginBottom: 12 }}>
                                <table style={{ width: '100%', borderCollapse: 'collapse', tableLayout: 'fixed', minWidth: 780 }}>
                                    <thead>
                                        <tr style={{ background: '#E9ECEF' }}>
                                            <th style={{ width: 50, padding: FEE_CELL_PAD }} />
                                            <th style={{ padding: FEE_CELL_PAD, textAlign: 'left', ...sectionHeadStyle }}>
                                                Platform Fee <span style={{ color: '#DC3545' }}>*</span>
                                            </th>
                                            <th style={{ padding: FEE_CELL_PAD, textAlign: 'left', ...sectionHeadStyle }}>
                                                Value <span style={{ color: '#DC3545' }}>*</span>
                                            </th>
                                            <th style={{ padding: FEE_CELL_PAD, textAlign: 'left', ...sectionHeadStyle }}>
                                                Cap (Max)
                                            </th>
                                            <th style={{ width: 50, padding: FEE_CELL_PAD }} />
                                        </tr>
                                    </thead>

                                    <ReactSortable tag="tbody" list={rows} setList={setRows} handle=".drag-handle" animation={150}>
                                        {rows.map((row, idx) => {
                                            const nameErr = touched[`row_${row.id}_platform_fee`] && errors[`row_${row.id}_platform_fee`];
                                            const valErr = touched[`row_${row.id}_value`] && errors[`row_${row.id}_value`];
                                            const isPct = row.formula_type === FEE_PCT_PLATFORM;
                                            const tdStyle = { padding: FEE_CELL_PAD, borderBottom: '1px solid #E9ECEF', verticalAlign: 'middle' };

                                            return (
                                                <tr key={row.id} style={{ background: idx % 2 === 0 ? '#fff' : '#F8F9FA' }}>
                                                    {/* Delete — the first fee row is fixed, so its icon stays disabled */}
                                                    <td style={{ ...tdStyle, textAlign: 'center' }}>
                                                        <button
                                                            type="button"
                                                            disabled={idx === 0}
                                                            onClick={() => removeRow(row.id)}
                                                            title="Delete"
                                                            style={{
                                                                display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                                                                width: 18, height: 18, padding: 0, border: 'none', background: 'none',
                                                                cursor: idx === 0 ? 'not-allowed' : 'pointer',
                                                                color: idx === 0 ? '#CED4DA' : '#6C757D',
                                                            }}
                                                        >
                                                            <Trash2 width={18} height={18} />
                                                        </button>
                                                    </td>

                                                    {/* Platform Fee name */}
                                                    <td style={tdStyle}>
                                                        <div style={{ display: 'flex', alignItems: 'center', gap: 6, height: 38, padding: FEE_INPUT_PAD, boxSizing: 'border-box', background: '#fff', borderRadius: 8, border: '1px solid ' + (nameErr ? '#DC3545' : '#212529') }}>
                                                            <input
                                                                type="text"
                                                                value={row.platform_fee_name}
                                                                onChange={e => handleRowChange(row.id, 'platform_fee_name', e.target.value)}
                                                                onBlur={() => { setTouched(prev => ({ ...prev, [`row_${row.id}_platform_fee`]: true })); validateRowsAndUpdate(); }}
                                                                style={{ flex: '1 1 0%', width: '100%', minWidth: 0, border: 'none', outline: 'none', padding: 0, background: 'transparent', fontSize: 14, fontFamily: 'Montserrat', color: '#212529' }}
                                                            />
                                                            {row.platform_fee_name && (
                                                                <button
                                                                    type="button"
                                                                    title="Clear"
                                                                    onClick={() => handleRowChange(row.id, 'platform_fee_name', '')}
                                                                    style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: 12, height: 12, padding: 0, border: 'none', background: 'none', color: '#6C757D', cursor: 'pointer', flexShrink: 0 }}
                                                                >
                                                                    <ClearIcon stroke="#6C757D" />
                                                                </button>
                                                            )}
                                                        </div>
                                                        {nameErr && <div style={errText}>{errors[`row_${row.id}_platform_fee`]}</div>}
                                                    </td>

                                                    {/* Value */}
                                                    <td style={tdStyle}>
                                                        {isPct ? (
                                                            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                                                <div style={platformPriceChipStyle}>
                                                                    <span>&#123;Platform Price&#125;</span>
                                                                    <span style={{ fontSize: 15 }}>×</span>
                                                                </div>
                                                                <div style={{ flex: 1, minWidth: 0 }}>
                                                                    <PctInput
                                                                        value={row.value}
                                                                        onValueChange={v => handleRowChange(row.id, 'value', v)}
                                                                        onBlur={() => {
                                                                            handleRowChange(row.id, 'value', fmtRate(row.value));
                                                                            setTouched(prev => ({ ...prev, [`row_${row.id}_value`]: true }));
                                                                            validateRowsAndUpdate();
                                                                        }}
                                                                        error={valErr}
                                                                    />
                                                                </div>
                                                            </div>
                                                        ) : (
                                                            <SgdInput
                                                                value={row.value}
                                                                onValueChange={v => handleRowChange(row.id, 'value', v)}
                                                                onBlur={() => {
                                                                    if (row.value && !isNaN(parseFloat(row.value))) {
                                                                        handleRowChange(row.id, 'value', parseFloat(row.value).toFixed(2));
                                                                    }
                                                                    setTouched(prev => ({ ...prev, [`row_${row.id}_value`]: true }));
                                                                    validateRowsAndUpdate();
                                                                }}
                                                                error={valErr}
                                                            />
                                                        )}
                                                        {valErr && <div style={errText}>{errors[`row_${row.id}_value`]}</div>}
                                                    </td>

                                                    {/* Cap (Max) — only a percentage fee can be capped */}
                                                    <td style={tdStyle}>
                                                        {isPct && (
                                                            <SgdInput
                                                                value={row.cap}
                                                                onValueChange={v => handleRowChange(row.id, 'cap', v)}
                                                                onBlur={() => {
                                                                    if (row.cap && !isNaN(parseFloat(row.cap))) {
                                                                        handleRowChange(row.id, 'cap', parseFloat(row.cap).toFixed(2));
                                                                    }
                                                                    validateRowsAndUpdate();
                                                                }}
                                                            />
                                                        )}
                                                    </td>

                                                    {/* Drag handle */}
                                                    <td style={{ ...tdStyle, textAlign: 'center' }}>
                                                        <span className="drag-handle" style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: 18, height: 18, cursor: 'grab', userSelect: 'none' }}>
                                                            <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 18 18" fill="none">
                                                                <path d="M3 9H15M3 13.5H15M3 4.5H15" stroke="#6C757D" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                                                            </svg>
                                                        </span>
                                                    </td>
                                                </tr>
                                            );
                                        })}
                                    </ReactSortable>
                                </table>
                            </div>

                            {/* Add Platform Fee */}
                            <div style={{ position: 'relative', display: 'inline-block' }} ref={addFeeRef}>
                                <button
                                    type="button"
                                    style={{ display: 'flex', alignItems: 'center', gap: 8, border: '1px solid #212529', borderRadius: 8, background: '#fff', padding: '10px', fontSize: 12, fontWeight: 600, cursor: 'pointer', color: '#212529' }}
                                    onClick={() => setAddFeeOpen(prev => !prev)}
                                >
                                    Add Platform Fee
                                    <Caret open={addFeeOpen} />
                                </button>
                                {addFeeOpen && (
                                    <div style={{ ...MENU_PANEL_STYLE, position: 'absolute', top: 'calc(100% + 4px)', left: 0, zIndex: 200, minWidth: '100%', whiteSpace: 'nowrap' }}>
                                        {FORMULA_TYPES.map(type => (
                                            <button
                                                key={type}
                                                type="button"
                                                style={MENU_ROW_STYLE}
                                                onMouseEnter={e => e.currentTarget.style.background = '#FFFEF0'}
                                                onMouseLeave={e => e.currentTarget.style.background = 'none'}
                                                onClick={() => { addRow(type); setAddFeeOpen(false); }}
                                            >
                                                {type}
                                            </button>
                                        ))}
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>

                    {/* ── Platform Price Markup ── */}
                    <div className="info-card new-info-overflow-auto">
                        <div className="info-header">
                            <h4 className="info-title">Platform Price Markup</h4>
                        </div>
                        <div className="info-content d-block">

                            <div style={{ overflowX: 'auto' }}>
                                <table style={{ width: '100%', borderCollapse: 'collapse', tableLayout: 'fixed', minWidth: 760 }}>
                                    <thead>
                                        <tr>
                                            <th style={{ width: 64, height: 45, boxSizing: 'border-box', padding: '15px 20px', background: '#E9ECEF' }} />
                                            <th style={{ width: 400, height: 45, boxSizing: 'border-box', padding: '15px 20px 15px 10px', background: '#E9ECEF', textAlign: 'left', ...sectionHeadStyle }}>
                                                Markup Range<span style={{ color: '#DC3545' }}>&nbsp;*</span>
                                            </th>
                                            <th style={{ height: 45, boxSizing: 'border-box', padding: '15px 20px 15px 10px', background: '#E9ECEF', textAlign: 'left', ...sectionHeadStyle }}>
                                                Markup Type<span style={{ color: '#DC3545' }}>&nbsp;*</span>
                                            </th>
                                            <th style={{ height: 45, boxSizing: 'border-box', padding: '15px 20px 15px 10px', background: '#E9ECEF', textAlign: 'left', ...sectionHeadStyle }}>
                                                Value
                                            </th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {markups.map((m, idx) => {
                                            const isLast = idx === markups.length - 1;
                                            const isFirst = idx === 0;
                                            const b = markupBounds(markups, idx);
                                            const e = bandErrors(markups, idx);
                                            // Only a genuinely contradictory range recolours the bound text; a
                                            // merely missing one leaves it black and shows the message alone.
                                            const boundColor = e.rangeError && e.rangeInvalid ? '#DC3545' : '#212529';
                                            const canDelete = !isFirst && markups.length > 1;
                                            const upperVal = m.upperText != null ? m.upperText : (m.upper == null ? '' : String(m.upper));
                                            const tdBase = { height: 60, boxSizing: 'border-box', padding: 10, borderBottom: '1px solid #DEE2E6', verticalAlign: 'middle' };

                                            return (
                                                <tr key={m.id} style={idx % 2 === 1 ? { background: '#F8F9FA' } : undefined}>
                                                    <td style={{ ...tdBase, padding: '10px 20px' }}>
                                                        {canDelete && (
                                                            <button
                                                                type="button"
                                                                title="Delete"
                                                                onClick={() => removeMarkup(idx)}
                                                                style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: 24, height: 24, padding: 0, border: 'none', background: 'none', cursor: 'pointer', color: '#6C757D' }}
                                                            >
                                                                <Trash2 size={16} />
                                                            </button>
                                                        )}
                                                    </td>

                                                    {/* Markup Range */}
                                                    <td style={tdBase}>
                                                        <div style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
                                                            <span style={{ color: '#212529', fontSize: 16, fontWeight: 600 }}>SGD</span>
                                                            <span style={{ fontSize: 16, fontWeight: 600, color: boundColor }}>{b.lowerInclusive ? '[' : '('}</span>
                                                            <span style={{ fontSize: 16, fontWeight: 600, fontVariantNumeric: 'tabular-nums', color: boundColor }}>{fmtBound(b.lower)}</span>
                                                            <span style={{ color: '#212529', fontSize: 16, fontWeight: 600 }}>,</span>

                                                            {isLast ? (
                                                                <>
                                                                    <span style={{ color: '#212529', fontSize: 16, fontWeight: 600, paddingLeft: 4 }}>&#8734;</span>
                                                                    <span style={{ color: '#212529', fontSize: 16, fontWeight: 600 }}>)</span>
                                                                </>
                                                            ) : (
                                                                <>
                                                                    <div style={{ display: 'flex', flex: '1 1 0%', flexDirection: 'column', gap: 4 }}>
                                                                        <div style={{ display: 'flex', alignSelf: 'stretch', alignItems: 'center', gap: 6, height: 40, minWidth: 50, padding: 10, boxSizing: 'border-box', background: '#fff', borderRadius: 8, border: '1px solid ' + (e.rangeError ? '#DC3545' : '#212529') }}>
                                                                            <input
                                                                                type="text"
                                                                                inputMode="decimal"
                                                                                value={upperVal}
                                                                                placeholder="0.00"
                                                                                onChange={ev => updateUpper(idx, stripMinus(ev.target.value))}
                                                                                onKeyDown={blockMinusKey}
                                                                                onBlur={() => blurUpper(idx)}
                                                                                style={{ flex: '1 1 0%', width: '100%', minWidth: 0, border: 'none', outline: 'none', padding: 0, background: 'transparent', color: '#212529', fontSize: 14, fontWeight: 400, fontVariantNumeric: 'tabular-nums', fontFamily: 'Montserrat' }}
                                                                            />
                                                                            {m.upper != null && (
                                                                                <button
                                                                                    type="button"
                                                                                    title="Clear"
                                                                                    onClick={() => updateUpper(idx, '')}
                                                                                    style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: 12, height: 12, padding: 0, border: 'none', background: 'none', color: '#6C757D', cursor: 'pointer', flexShrink: 0 }}
                                                                                >
                                                                                    <ClearIcon stroke="#6C757D" />
                                                                                </button>
                                                                            )}
                                                                        </div>
                                                                        {e.rangeError && (
                                                                            <div style={{ display: 'flex', alignSelf: 'stretch', alignItems: 'center', padding: '0 5px' }}>
                                                                                <p style={{ flex: '1 1 0%', margin: 0, color: '#DC3545', fontSize: 12, fontWeight: 400 }}>{e.rangeErrorMsg}</p>
                                                                            </div>
                                                                        )}
                                                                    </div>
                                                                    {/* Owns the boundary value: flipping this also flips the next band's opening bracket */}
                                                                    <button
                                                                        type="button"
                                                                        title="Toggle inclusive / exclusive upper bound"
                                                                        onClick={() => toggleUpper(idx)}
                                                                        style={{
                                                                            width: 40, height: 40, flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center',
                                                                            padding: 10, boxSizing: 'border-box', border: '1px solid #6C757D', borderRadius: 8,
                                                                            boxShadow: '0px 1px 4px 0px rgba(0,0,0,0.1)', fontFamily: 'Montserrat', fontWeight: 600, fontSize: 16,
                                                                            cursor: 'pointer', color: '#212529', background: m.includeUpper ? '#FFFBCF' : '#E9ECEF',
                                                                        }}
                                                                    >
                                                                        {m.includeUpper ? ']' : ')'}
                                                                    </button>
                                                                </>
                                                            )}
                                                        </div>
                                                    </td>

                                                    {/* Markup Type */}
                                                    <td style={tdBase}>
                                                        <div style={{ display: 'flex', flexDirection: 'column', gap: 4, minWidth: 200 }}>
                                                            <MarkupTypeSelect
                                                                value={m.type}
                                                                error={e.typeError}
                                                                onChange={val => updateType(idx, val)}
                                                                onTouch={() => markupTouch(idx, 'type')}
                                                            />
                                                            {e.typeError && (
                                                                <div style={{ display: 'flex', alignSelf: 'stretch', alignItems: 'center', padding: '0 5px' }}>
                                                                    <p style={{ flex: '1 1 0%', margin: 0, color: '#DC3545', fontSize: 12, fontWeight: 400 }}>Markup Type is required</p>
                                                                </div>
                                                            )}
                                                        </div>
                                                    </td>

                                                    {/* Value — shape follows the chosen Markup Type */}
                                                    <td style={tdBase}>
                                                        {(!m.type || m.type === MARKUP_NONE) && (
                                                            <span style={{ color: '#212529', fontSize: 12, fontWeight: 400 }}>-</span>
                                                        )}

                                                        {m.type === MARKUP_FIXED && (
                                                            <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                                                                <div style={{ display: 'flex', alignSelf: 'stretch', alignItems: 'center', minWidth: 10, background: '#fff', borderRadius: 8, overflow: 'hidden', border: '1px solid ' + (e.valueError ? '#DC3545' : '#212529') }}>
                                                                    <div style={{ height: 40, display: 'flex', alignItems: 'center', padding: 10, background: '#F8F9FA', borderRight: '1px solid #212529', boxSizing: 'border-box' }}>
                                                                        <span style={{ color: '#212529', fontSize: 14, fontWeight: 400 }}>SGD</span>
                                                                    </div>
                                                                    <input
                                                                        type="number"
                                                                        step="any"
                                                                        min="0"
                                                                        value={m.param == null ? '' : m.param}
                                                                        placeholder="0.00"
                                                                        onChange={ev => updateParam(idx, stripMinus(ev.target.value))}
                                                                        onKeyDown={blockMinusKey}
                                                                        onBlur={() => markupTouch(idx, 'value')}
                                                                        style={{ flex: '1 1 0%', width: '100%', minWidth: 0, height: 40, padding: 10, boxSizing: 'border-box', border: 'none', outline: 'none', background: 'transparent', color: '#212529', fontSize: 14, fontWeight: 400, fontVariantNumeric: 'tabular-nums', fontFamily: 'Montserrat' }}
                                                                    />
                                                                </div>
                                                                {e.valueError && (
                                                                    <div style={{ display: 'flex', alignSelf: 'stretch', alignItems: 'center', padding: '0 5px' }}>
                                                                        <p style={{ flex: '1 1 0%', margin: 0, color: '#DC3545', fontSize: 12, fontWeight: 400 }}>Value is required</p>
                                                                    </div>
                                                                )}
                                                            </div>
                                                        )}

                                                        {m.type === MARKUP_PERCENT && (
                                                            <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                                                                <div style={{ display: 'flex', alignSelf: 'stretch', alignItems: 'center', minWidth: 50, background: '#fff', borderRadius: 8, overflow: 'hidden', border: '1px solid ' + (e.valueError ? '#DC3545' : '#212529') }}>
                                                                    <input
                                                                        type="number"
                                                                        step="any"
                                                                        min="0"
                                                                        value={m.param == null ? '' : m.param}
                                                                        placeholder="0.00"
                                                                        onChange={ev => updateParam(idx, stripMinus(ev.target.value))}
                                                                        onKeyDown={blockMinusKey}
                                                                        onBlur={() => markupTouch(idx, 'value')}
                                                                        style={{ flex: '1 1 0%', width: '100%', minWidth: 0, height: 40, padding: 10, boxSizing: 'border-box', border: 'none', outline: 'none', background: 'transparent', color: '#212529', fontSize: 14, fontWeight: 400, fontVariantNumeric: 'tabular-nums', fontFamily: 'Montserrat' }}
                                                                    />
                                                                    <div style={{ height: 40, display: 'flex', alignItems: 'center', padding: 10, background: '#F8F9FA', borderLeft: '1px solid #6C757D', boxSizing: 'border-box' }}>
                                                                        <span style={{ color: '#6C757D', fontSize: 14, fontWeight: 400 }}>%</span>
                                                                    </div>
                                                                </div>
                                                                {e.valueError && (
                                                                    <div style={{ display: 'flex', alignSelf: 'stretch', alignItems: 'center', padding: '0 5px' }}>
                                                                        <p style={{ flex: '1 1 0%', margin: 0, color: '#DC3545', fontSize: 12, fontWeight: 400 }}>Value is required</p>
                                                                    </div>
                                                                )}
                                                            </div>
                                                        )}
                                                    </td>
                                                </tr>
                                            );
                                        })}
                                    </tbody>
                                </table>
                            </div>

                            <div style={{ display: 'flex', flexDirection: 'column', gap: 10, alignItems: 'flex-start', marginTop: 10 }}>
                                <span style={{ color: '#6C757D', fontSize: 14, fontWeight: 400, fontFamily: 'Montserrat' }}>
                                    Notation: [] inclusive, () exclusive; e.g. (160, 800] means 160 &lt; Platform Price &#8804; 800.
                                </span>
                                <button
                                    type="button"
                                    onClick={addMarkup}
                                    style={{ height: 34, minWidth: 100, display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 10, padding: 10, background: '#fff', border: '1px solid #212529', borderRadius: 8, boxShadow: '0px 1px 4px 0px rgba(0,0,0,0.1)', color: '#212529', fontFamily: 'Montserrat', fontSize: 12, fontWeight: 600, cursor: 'pointer' }}
                                >
                                    Add Markup
                                </button>
                            </div>
                        </div>
                    </div>

                    {/* ── Test Calculation ── */}
                    <div className="info-card new-info-overflow-auto">
                        <div className="info-header">
                            <h4 className="info-title">Test Calculation</h4>
                        </div>
                        <div className="info-content d-block">

                            <div style={{ ...TC_GRID, marginBottom: 18 }}>
                                <div>
                                    <label style={{ ...sectionHeadStyle, display: 'block', marginBottom: 8 }}>
                                        Base Price <span style={{ color: '#DC3545' }}>*</span>
                                    </label>
                                    <SgdInput
                                        value={testBasePrice}
                                        onValueChange={v => { setTestBasePrice(v); if (testBaseErr) setTestBaseErr(''); }}
                                        onBlur={() => {
                                            if (testBasePrice && !isNaN(parseFloat(testBasePrice))) {
                                                setTestBasePrice(parseFloat(testBasePrice).toFixed(2));
                                            }
                                        }}
                                        error={!!testBaseErr}
                                    />
                                    {testBaseErr && <div style={errText}>{testBaseErr}</div>}
                                </div>
                                <div>
                                    {/* Mirrors the Base Price label's height so the buttons line up
                                        with the input, and stay put when an error line appears. */}
                                    <label aria-hidden="true" style={{ ...sectionHeadStyle, display: 'block', marginBottom: 8, visibility: 'hidden' }}>&nbsp;</label>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
                                        <button
                                            type="button"
                                            onClick={runCalculation}
                                            style={{ height: 38, minWidth: 100, padding: '10px', borderRadius: 8, fontSize: 12, fontWeight: 600, fontFamily: 'Montserrat', background: '#212529', color: '#fff', border: 'none', cursor: 'pointer' }}
                                        >
                                            Calculate
                                        </button>
                                        <button
                                            type="button"
                                            onClick={() => setDetailOpen(o => !o)}
                                            style={{ height: 38, background: 'none', border: 'none', cursor: 'pointer', color: '#6C757D', fontFamily: 'Montserrat', fontSize: 12, fontWeight: 600, display: 'flex', alignItems: 'center', gap: 6, padding: 0 }}
                                        >
                                            {detailOpen ? 'Collapse Detail' : 'Expand Detail'}
                                            <svg xmlns="http://www.w3.org/2000/svg" style={{ transition: 'transform 0.2s', transform: detailOpen ? 'rotate(180deg)' : 'rotate(0deg)' }} width="18" height="18" viewBox="0 0 18 18" fill="none">
                                                <path d="M4.5 6.75L9 11.25L13.5 6.75" stroke="#6C757D" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                                            </svg>
                                        </button>
                                    </div>
                                </div>
                            </div>

                            {/* Breakdown — shown whenever expanded, with "-" standing in
                                for every figure until Calculate produces a result. */}
                            {detailOpen && (
                                <div style={{ ...TC_GRID, marginBottom: 18 }}>

                                    {/* Left: reverse derivation, then the markup step */}
                                    <div style={{ border: `1px solid ${PANEL_BORDER}`, borderRadius: 8, background: PANEL_BG, padding: '15px 20px' }}>
                                        <PanelLabel n="01">Reverse-Derive Platform Price (Before Markup)</PanelLabel>
                                        <DetailLine label="Platform Price (Before Markup)" value={calc ? money(calc.beforeMarkup) : null} strong boldBorder />
                                        {/* Fee lines come from the Platform Fee rows above — no rule between
                                            them, only the bold rules framing the group top and bottom. */}
                                        {rows.length > 0 ? rows.map((r, i) => (
                                            <DetailLine
                                                key={r.id}
                                                label={r.platform_fee_name || '—'}
                                                value={calc && calc.reverseFees[i] ? money(calc.reverseFees[i].amount) : null}
                                                sign="-"
                                                last={i < rows.length - 1}
                                                boldBorder={i === rows.length - 1}
                                            />
                                        )) : (
                                            <DetailLine label="" value={null} last />
                                        )}
                                        <DetailLine label="Base Price" value={calc ? money(calc.base) : null} strong color={PANEL_GREEN} last />

                                        <StepDivider />

                                        <PanelLabel n="02">Apply Markup to Platform Price (Final)</PanelLabel>
                                        <DetailLine label="Platform Price (Before Markup)" value={calc ? money(calc.beforeMarkup) : null} strong />
                                        <DetailLine label="Markup" value={calc ? money(calc.markupAmt) : null} sign={calc && calc.markupAmt < 0 ? '-' : '+'} />
                                        <DetailLine label="Platform Price (Final)" note={`*${ruleLabel}`} value={calc ? money(calc.finalPrice) : null} strong last />
                                    </div>

                                    {/* Right: expected profit from the final price */}
                                    <div style={{ border: `1px solid ${PANEL_BORDER}`, borderRadius: 8, background: PANEL_BG, padding: '15px 20px' }}>
                                        <PanelLabel n="03">Expected Profit from Platform Price (Final)</PanelLabel>
                                        <DetailLine label="Platform Price (Final)" value={calc ? money(calc.finalPrice) : null} strong boldBorder />
                                        {rows.length > 0 ? rows.map((r, i) => (
                                            <DetailLine
                                                key={r.id}
                                                label={r.platform_fee_name || '—'}
                                                value={calc && calc.finalFees[i] ? money(calc.finalFees[i].amount) : null}
                                                sign="-"
                                                last={i < rows.length - 1}
                                                boldBorder={i === rows.length - 1}
                                            />
                                        )) : (
                                            <DetailLine label="" value={null} last />
                                        )}
                                        <DetailLine label="Base Price" value={calc ? money(calc.base) : null} sign="-" />
                                        <DetailLine label="Expected Profit" value={calc ? money(calc.profit) : null} sign={calc && calc.profit < 0 ? '-' : ''} strong last />
                                    </div>
                                </div>
                            )}

                            {/* Summary — columns mirror the controls row above */}
                            <div style={TC_GRID}>
                                <div>
                                    <div style={{ ...sectionHeadStyle, marginBottom: 6 }}>Platform Price (Final)</div>
                                    <div style={{ fontSize: 20, fontWeight: 700, color: '#212529', fontFamily: 'Montserrat', fontVariantNumeric: 'tabular-nums' }}>
                                        SGD {calc ? money(calc.finalPrice) : '0.00'}
                                    </div>
                                </div>
                                <div>
                                    <div style={{ ...sectionHeadStyle, marginBottom: 6 }}>Expected Profit</div>
                                    <div style={{ fontSize: 20, fontWeight: 700, color: '#212529', fontFamily: 'Montserrat', fontVariantNumeric: 'tabular-nums' }}>
                                        {calc && calc.profit < 0 ? '-' : ''}SGD {calc ? money(calc.profit) : '0.00'}
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* ── Notes ── */}
                    <div className="info-card">
                        <div className="info-header">
                            <h4 className="info-title">Notes</h4>
                        </div>
                        <div className="info-content">
                            <div className="info-inner common-textarea">
                                <textarea
                                    className="form-control w-100 common-textarea-all"
                                    rows="3"
                                    name="notes"
                                    value={formData.notes}
                                    onChange={handleChange}
                                    style={{ border: '1px solid #6C757D' }}
                                />
                            </div>
                        </div>
                        <UnsavedChangesPrompt isDirty={isDirty && !isDiscarding} entityName="Pricing Template" />
                    </div>

                </div>
            </div>
        </div>
    );
}
